<template>
  <a-card id="channel-publication">
    <h4 class="mb-4">
      {{ $t('product.channel_publication') }}
    </h4>
    
    <div class="selected-channels">
      <div
        v-for="(channel, index) in channels"
        :key="index"
        class="list-channel"
        :style="{ marginLeft: index > 0 ? '12px' : '0' }"
      >
        <a-popconfirm
          :title="$t('product.confirm_remove_channel')"
          ok-text="OK"
          :cancel-text="$t('utils.cancel')"
          @confirm="onRemoveChannel(index)"
        >
          <a-icon v-if="!isLoading" type="close-circle" theme="filled" />
        </a-popconfirm>

        <img src="@/assets/product/no-image.webp" />
        <a-tooltip :title="channel.id ? `Channel ID: ${channel.id}` : undefined" placement="top">
          <p class="mb-0 channel-title">
            {{ channel.title }}
          </p>
        </a-tooltip>
      </div>
      <div 
        v-if="unselectedChannels.length" 
        class="list-channel" 
        :style="{ marginLeft: channels.length > 0 ? '12px' : '0', backgroundColor: '#fafafa', cursor: 'pointer' }"
        @click="channelModalVisible = true"
      >
        <a-icon type="plus" />
        <p class="mb-0" style="font-size: 14px; color: #666">
          {{ $t('product.select_channel') }}
        </p>
      </div>
    </div>

    <a-card v-if="marketplaceList.length" :title="$t('channel.category.default')" class="channel-category mt-4">
      <div v-for="(channel, idx) in marketplaceList" :key="channel.id">
        <a-form-model-item
          :label="$t('channel.category.' + channel.code)"
          :prop="'channels.' + idx + '.category'"
          :rules="[{ required: true, message: $t('product.must_be_selected', { name: $t('channel.category.' + channel.code) }), trigger: 'change' }]"
        >
          <a-spin :spinning="channel.loading">
            <a-cascader
              v-model="channel.category"
              class="text-left w-100 h-48px"
              popup-class-name="kategori-options"
              :field-names="channel.fields"
              :options="channel.categories"
              :disabled="channel.isMapping"
              placeholder=""
              @change="fetchAttributes($event, channel)"
            />
          </a-spin>
        </a-form-model-item>
        <a-row v-if="channel.attrLoading" :gutter="16">
          <a-col :span="12">
            <a-skeleton :loading="channel.attrLoading" active />
          </a-col>
          <a-col :span="12">
            <a-skeleton :loading="channel.attrLoading" active />
          </a-col>
        </a-row>
        <a-row v-else :gutter="16">
          <template v-for="(attribute, index) in channel.attributes">
            <a-col
              v-if="!attribute.original_attribute_name.toLowerCase().includes('merek')"
              :key="attribute.attribute_id"
              :span="12"
            >
              <a-form-model-item :label="attribute.display_attribute_name">
                <template v-if="attribute.input_type === 'TEXT_FILED'">
                  <a-date-picker
                    v-if="attribute.input_validation_type === 'DATE_TYPE'"
                    v-model="attribute.value"
                    format="DD/MM/YYYY"
                    size="large"
                    class="w-100"
                    @change="inputAttribute(attribute, index, channel)"
                  />
                  <a-row v-else>
                    <a-col :span="attribute.format_type === 'QUANTITATIVE' && attribute.attribute_unit.length ? 18 : 24">
                      <a-input
                        v-model="attribute.value"
                        size="large"
                        @change="inputAttribute(attribute, index, channel)"
                        @keypress="numberDecimal($event, attribute)"
                      />
                    </a-col>
                    <a-col v-if="attribute.format_type === 'QUANTITATIVE' && attribute.attribute_unit.length" :span="6">
                      <a-select
                        v-model="attribute.unit"
                        size="large"
                        class="quantitative"
                        style="margin-left: -3px"
                        @change="inputAttribute(attribute, index, channel)"
                      >
                        <a-select-option
                          v-for="unit in attribute.attribute_unit"
                          :key="unit"
                          :value="unit"
                        >
                          {{ unit }}
                        </a-select-option>
                      </a-select>
                    </a-col>
                  </a-row>
                </template>
                <a-select
                  v-else
                  v-model="attribute.value"
                  :mode="attribute.mode"
                  size="large"
                  class="select-antd-default"
                  label-in-value
                  allow-clear
                  @change="selectAttribute($event, attribute, index, channel)"
                >
                  <a-select-option
                    v-for="attr in attribute.attribute_value_list"
                    :key="attr.value_id"
                    :value="attr.value_id"
                    :disabled="isDisabled(attribute, attr)"
                  >
                    {{ attr.display_value_name }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </template>
        </a-row>
      </div>
    </a-card>

    <a-modal
      :visible="channelModalVisible"
      :closable="false"
      :footer="null"
      :title="$t('product.select_channel')"
      :width="600"
      centered
    >
      <div class="channel-modal-container">
        <div class="channel-list-scroll">
          <a-checkbox-group v-model="selectedChannels" style="width: 100%">
            <a-collapse class="sticky-collapse" :default-active-key="'0'">
              <a-collapse-panel 
                v-for="(group, key) in groupedChannels" 
                :key="key" 
                :header="group.name"
              >
                <div class="channel-list">
                  <a-tooltip
                    v-for="channel in group.items"
                    :key="channel.originalIndex"
                    :title="channel.id ? `Channel ID: ${channel.id}` : undefined"
                    placement="top"
                  >
                    <a-checkbox
                      :value="channel.originalIndex"
                      :disabled="isLoading"
                    >
                      <img src="@/assets/product/no-image.webp" />
                      <p class="mb-0">
                        {{ channel.title }}
                      </p>
                    </a-checkbox>
                  </a-tooltip>
                </div>
              </a-collapse-panel>
            </a-collapse>
          </a-checkbox-group>
        </div>

        <div class="channel-modal-footer">
          <transition name="fade">
            <div v-if="selectedChannels.length" class="selected-summary mt-4">
              <div class="summary-header">
                <div style="cursor: pointer; display: flex; align-items: center" @click="isSummaryVisible = !isSummaryVisible">
                  <a-icon :type="isSummaryVisible ? 'down' : 'right'" style="font-size: 12px; margin-right: 8px" />
                  <strong>{{ $t('product.selected_channels') }}</strong>
                  <a-tag color="blue" style="margin-left: 8px">
                    {{ selectedChannels.length }}
                  </a-tag>
                </div>
                <a-button type="link" size="small" @click="isSummaryVisible = !isSummaryVisible">
                  {{ isSummaryVisible ? $t('utils.hide') : $t('utils.show') }}
                </a-button>
              </div>
              <div v-if="isSummaryVisible" class="summary-tags">
                <div
                  v-for="(ch, i) in selectedChannelsList"
                  :key="i"
                  class="channel-mini-tag"
                >
                  <div class="tag-body">
                    <div class="tag-title">
                      {{ ch.title }}
                    </div>
                    <div class="tag-code">
                      {{ ch.code }}
                    </div>
                  </div>
                  <a-icon type="close-circle" theme="filled" class="tag-close" @click="onDeselectChannel(ch.index)" />
                </div>
              </div>
            </div>
          </transition>

          <a-row :gutter="16" class="mt-4">
            <a-col :span="12">
              <a-button
                type="primary"
                size="large"
                block
                ghost
                @click="onCloseChannelModal"
              >
                {{ $t('utils.cancel') }}
              </a-button>
            </a-col>
            <a-col :span="12">
              <a-button
                type="primary"
                size="large"
                block
                :disabled="isLoading || !selectedChannels.length"
                @click="onSelectChannel"
              >
                {{ $t('product.add_channel') }}
              </a-button>
            </a-col>
          </a-row>
        </div>
      </div>
    </a-modal>
  </a-card>
</template>

<script>
import { getChannelCategories, getChannelAttributes } from '@/api/channels/index'
import buildTree from '@/utils/buildTree'

export default {
  name: 'ChannelPublication',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    businessId: {
      type: String,
      default: '',
    },
    brandId: {
      type: String,
      default: '',
    },
    channelList: {
      type: Array,
      default: () => [],
    },
    channelCategories: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      channelModalVisible: false,
      selectedChannels: [],
      unselectedChannels: this.channelList,
      marketplaceCode: ['shopee', 'tiktok', 'tokopedia'],
      marketplaceList: [],
      categoryList: {},
      isMultiple: ['MULTIPLE_SELECT', 'MULTIPLE_SELECT_COMBO_BOX'],
      isSummaryVisible: true,
    }
  },
  computed: {
    channels: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      },
    },
    idBusiness() {
      return this.businessId
    },
    isLoading() {
      return this.loading
    },
    groupedChannels() {
      const groups = {}
      this.unselectedChannels.forEach((channel, index) => {
        const groupCode = channel.groupCode || 'other'
        const name = channel.groupCode 
          ? channel.groupCode.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') 
          : 'Other'
        if (!groups[groupCode]) {
          groups[groupCode] = {
            name,
            items: [],
          }
        }
        groups[groupCode].items.push({
          ...channel,
          originalIndex: index,
        })
      })

      return groups
    },
    selectedChannelsList() {
      return this.selectedChannels.map(index => {
        const ch = this.unselectedChannels[index]
        if (!ch) return null
        return {
          title: ch.title,
          code: ch.sales_channel?.code || ch.code || 'other',
          index,
        }
      }).filter(Boolean)
    },
  },
  watch: {
    brandId(brand) {
      this.selectBrandAttribute(brand)
    },
    channelList(value) {
      this.unselectedChannels = this.channels.length
        ? value.filter(channel => {
            const findChannel = this.channels.find(ch => ch.id === channel.id)
            return !findChannel?.id
          })
        : value
    },
    channelCategories(category) {
      this.marketplaceList.forEach((mp, index) => {
        mp.category = []
        mp.attribute = []
        mp.attributes = []
        mp.isMapping = false
        if(category?.[mp.sales]) {
          this.mappingCategories(this.categoryList[mp.sales], category[mp.sales], index)
        }
      })
    },
  },
  methods: {
    async fetchAttributes(category, channel) {
      channel.attribute = []
      channel.attributes = []
      if(category.length) {
        channel.attrLoading = true
        const bodyVal = {
          business_id: this.idBusiness,
          channel_code: channel.code,
          channel_id: channel.id,
          channel_type: channel.code.includes('tokopedia')
            ? 'annotation'
            : 'attributes',
          params: {
            shop_id: channel.code.includes('tiktok')
              ? 'c9ee7774-8494-45e3-8398-bf3e528be375'
              : undefined,
            category_id: category[3] || category[2] || category[1] || category[0],
          },
        }
        
        await getChannelAttributes(bodyVal)
          .then(({ data }) => {
            let arrMandatory = []
            let arrNotMandatory = []
            if(channel.code.includes('shopee')) {
              const list = data ? data : []
              list?.forEach(attr => {
                const unit = attr.format_type === 'QUANTITATIVE' && attr.attribute_unit.length
                  ? attr.attribute_unit[0]
                  : undefined
                const obj = {
                  ...attr,
                  attribute_type: undefined,
                  value: undefined,
                  mode: this.isMultiple.includes(attr.input_type)
                    ? 'multiple'
                    : 'default',
                  unit,
                }
                if(attr.is_mandatory) {
                  arrMandatory.push(obj)
                } else {
                  arrNotMandatory.push(obj)
                }
              })
              channel.attributes = arrMandatory.concat(arrNotMandatory)
            } else if(channel.code.includes('tokopedia')) {
              const list = data?.data ? data.data : []
              list?.forEach((attr, index) => {
                const obj = {
                  attribute_type: undefined,
                  attribute_id: index,
                  original_attribute_name: attr.variant,
                  display_attribute_name: attr.variant,
                  is_mandatory: false,
                  format_type: 'NORMAL',
                  input_validation_type: 'STRING_TYPE',
                  mode: 'default',
                  input_type: 'COMBO_BOX',
                  max_input_value_number: 1,
                  attribute_value_list: attr?.values?.length
                    ? attr.values.map(val => {
                        return {
                          value_id: val.id,
                          original_value_name: val.name,
                          display_value_name: val.name,
                        }
                      })
                    : [],
                }
                arrNotMandatory.push(obj)
              })
              channel.attributes = arrMandatory.concat(arrNotMandatory)
              selectBrandAttribute(props.product.detail.brand_id)
            } else {
              const list = data?.data?.attributes ? data.data.attributes : []
              list?.forEach(attr => {
                if(attr.attribute_type == '3') {
                  const obj = {
                    attribute_type: attr.attribute_type,
                    attribute_id: attr.id,
                    original_attribute_name: attr.name,
                    display_attribute_name: attr.name,
                    is_mandatory: attr.input_type.is_mandatory,
                    format_type: 'NORMAL',
                    input_validation_type: 'STRING_TYPE',
                    mode: attr.input_type.is_multiple_selected && !attr?.values?.length
                      ? 'tags'
                      : attr.input_type.is_multiple_selected && attr?.values?.length
                      ? 'multiple'
                      : 'default',
                    input_type: attr.input_type.is_multiple_selected
                      ? 'MULTIPLE_SELECT_COMBO_BOX'
                      : !attr.input_type.is_multiple_selected && !attr?.values?.length
                      ? 'TEXT_FILED'
                      : 'COMBO_BOX',
                    max_input_value_number: attr.input_type.is_multiple_selected
                      ? 20
                      : 1,
                    attribute_value_list: attr?.values?.length
                      ? attr.values.map(val => {
                          return {
                            value_id: val.id,
                            original_value_name: val.name,
                            display_value_name: val.name,
                          }
                        })
                      : [],
                  }
                  if(attr.input_type.is_mandatory) {
                    arrMandatory.push(obj)
                  } else {
                    arrNotMandatory.push(obj)
                  }
                }
              })
              channel.attributes = arrMandatory.concat(arrNotMandatory)
            }
          })
          .finally(() => channel.attrLoading = false)
      }
      this.emitValue()
    },
    async addCategory(channel, index) {
      await getChannelCategories({
        business_id: this.idBusiness,
        channel_code: channel.code,
        channel_id: channel.id,
      })
      .then(({ data }) => {
        const rawList = Array.isArray(data) ? data : (data?.data || data?.category_list || [])
        const list = Array.isArray(rawList) ? rawList : (rawList?.category_list || [])
        const parent_id = channel.code.includes('shopee')
          ? 'parent_category_id'
          : 'parent_id'
        this.categoryList[channel.sales] = list
        this.marketplaceList[index].categories = buildTree(list, 'category_id', parent_id)
        if(this.channelCategories?.[channel.sales]) {
          this.mappingCategories(list, this.channelCategories[channel.sales], index)
        }
      })
      .finally(() => this.marketplaceList[index].loading = false)
    },
    onSelectChannel() {
      this.selectedChannels.forEach(channelIndex => {
        this.channels.push(this.unselectedChannels[channelIndex])
        const isMarketplace = this.marketplaceCode.find(mp => new RegExp(`^${mp}(_.*)?$`).test(this.unselectedChannels[channelIndex].code))
        if(this.unselectedChannels[channelIndex]?.code && isMarketplace) {
          const { id, code, sales, ...rest } = this.unselectedChannels[channelIndex]
          this.marketplaceList.push({
            ...rest,
            id,
            code,
            sales,
            category: [],
            categories: [],
            attribute: [],
            attributes: [],
            fields: code.includes('shopee')
              ? { label: 'display_category_name', value: 'category_id', children: 'children' }
              : { label: 'name', value: 'category_id', children: 'children' },
            isMapping: false,
            loading: true,
            attrLoading: false,
          })
          this.addCategory({ id, code, sales }, (this.marketplaceList.length - 1))
        }
        this.unselectedChannels.splice(channelIndex, 1)
      })
      this.selectedChannels = []
      this.channelModalVisible = false
    },
    onCloseChannelModal() {
      this.channelModalVisible = false
      this.selectedChannels = []
    },
    onDeselectChannel(index) {
      const pos = this.selectedChannels.indexOf(index)
      if (pos > -1) {
        this.selectedChannels.splice(pos, 1)
      }
    },
    onRemoveChannel(index) {
      this.unselectedChannels.push(this.channels[index])
      this.channels.splice(index, 1)
    },
    mappingCategories(categories, keyword, index) {
      const findCategory = categories.find(category => category.category_id == keyword)
      if(findCategory?.category_id) {
        this.marketplaceList[index].category.unshift(findCategory.category_id)
        if(findCategory.parent_category_id == 0) {
          this.marketplaceList[index].isMapping = true
          this.fetchAttributes(this.marketplaceList[index].category, this.marketplaceList[index])
        } else {
          this.mappingCategories(categories, findCategory.parent_category_id, index)
        }
      }
    },
    selectBrandAttribute(brand) {
      if(!brand || !this.marketplaceList.length) return

      const mpIndex = this.marketplaceList.findIndex(mp => mp.code.includes('tokopedia'))
      if(mpIndex > -1) {
        let found = false
        const attrIndex = this.marketplaceList[mpIndex].attributes.findIndex(attr => attr.original_attribute_name.toLowerCase().includes('merek'))
        if(attrIndex > -1) {
          const findValue = this.marketplaceList[mpIndex].attributes[attrIndex].attribute_value_list.find(value => value.value_id == brand || value.original_value_name == brand)
          if(findValue?.value_id) {
            const attributeIndex = this.marketplaceList[mpIndex].attribute.findIndex(attribute => attribute.attribute_name.toLowerCase().includes('merek'))
            if(attributeIndex > -1) this.marketplaceList[mpIndex].attribute.splice(attributeIndex, 1)

            this.marketplaceList[mpIndex].attribute.push({
              attribute_id: 999,
              attribute_name: "Merek",
              attribute_type: undefined,
              attribute_value_list: [{
                original_value_name: findValue.original_value_name,
                value_id: findValue.value_id,
              }],
              is_mandatory: false,
            })
            found = true
          }
        }

        if(!found) {
          const attributeIndex = this.marketplaceList[mpIndex].attribute.findIndex(attribute => attribute.attribute_name.toLowerCase().includes('merek'))
          if(attributeIndex > -1) this.marketplaceList[mpIndex].attribute.splice(attributeIndex, 1)
        }
        
        this.emitValue()
      }
    },
    selectAttribute(value, attr, index, channel) {
      let attribute_value_list = []
      if(this.isMultiple.includes(attr.input_type)) {
        attribute_value_list = value.map(val => {
          return {
            value_id: val.key,
            original_value_name: val.label.trim(),
          }
        })
      } else {
        attribute_value_list = value?.key
          ? [{
              value_id: value.key,
              original_value_name: value.label.trim(),
            }]
          : []
      }
      
      const channelIndex = this.marketplaceList.findIndex(mp => mp.code === channel.code)
      if(channelIndex > -1) {
        const attrIndex = this.marketplaceList[channelIndex].attributes.findIndex(a => a.attribute_id === attr.attribute_id)
        if(attrIndex > -1) {
          this.$set(this.marketplaceList[channelIndex].attributes[attrIndex], 'value', value)
        }
        
        const apiAttrIndex = this.marketplaceList[channelIndex].attribute.findIndex(a => a.attribute_id === attr.attribute_id)
        if(apiAttrIndex > -1) {
          this.$set(this.marketplaceList[channelIndex].attribute, apiAttrIndex, {
            attribute_id: attr.attribute_id,
            attribute_value_list,
            is_mandatory: attr.is_mandatory,
          })
        } else {
          this.marketplaceList[channelIndex].attribute.push({
            attribute_id: attr.attribute_id,
            attribute_value_list,
            is_mandatory: attr.is_mandatory,
          })
        }
      }
      this.emitValue()
    },
    inputAttribute(attr, index, channel) {
      const attribute_value_list = [{
        value_id: 0,
        original_value_name: attr.input_validation_type === 'DATE_TYPE'
          ? this.$moment(attr.value).unix()
          : attr.value,
        value_unit: attr.format_type === 'QUANTITATIVE' && attr.attribute_unit.length
          ? attr.unit
          : undefined,
      }]

      const channelIndex = this.marketplaceList.findIndex(mp => mp.code === channel.code)
      if(channelIndex > -1) {
        const attrIndex = this.marketplaceList[channelIndex].attributes.findIndex(a => a.attribute_id === attr.attribute_id)
        if(attrIndex > -1) {
          this.$set(this.marketplaceList[channelIndex].attributes[attrIndex], 'value', attr.value)
          this.$set(this.marketplaceList[channelIndex].attributes[attrIndex], 'unit', attr.unit)
        }
        
        const apiAttrIndex = this.marketplaceList[channelIndex].attribute.findIndex(a => a.attribute_id === attr.attribute_id)
        if(apiAttrIndex > -1) {
          this.$set(this.marketplaceList[channelIndex].attribute, apiAttrIndex, {
            attribute_id: attr.attribute_id,
            attribute_value_list,
            is_mandatory: attr.is_mandatory,
          })
        } else {
          this.marketplaceList[channelIndex].attribute.push({
            attribute_id: attr.attribute_id,
            attribute_value_list,
            is_mandatory: attr.is_mandatory,
          })
        }
      }
      this.emitValue()
    },
    isDisabled(attribute, attr) {
      if(attribute.value && this.isMultiple.includes(attribute.input_type)) {
        const index = attribute.value.findIndex(val => val.key == attr.value_id)
        return this.isMultiple.includes(attribute.input_type)
          && attribute.value.length == attribute.max_input_value_number
          && index < 0
      }
      return false
    },
    numberDecimal(event, attr) {
      const keyCode = (event.which) ? event.which : event.keyCode
      const { value } = event.target
      if(attr.input_validation_type === 'FLOAT_TYPE') {
        if (!value && keyCode === 46) {
          event.preventDefault()
        }

        if ((keyCode < 48 || keyCode > 57) && (keyCode !== 46 || value.indexOf('.') > -1)) {
          event.preventDefault()
        }

        if (value && value.indexOf('.') > -1 && value.split('.')[1].length > 1) {
          event.preventDefault()
        }
      } else if(attr.input_validation_type === 'INT_TYPE') {
        if (keyCode > 31 && (keyCode < 48 || keyCode > 57)) {
          event.preventDefault()
        }
      }
    },
    emitValue() {
      const cleanedData = this.marketplaceList.map(mp => {
        const { categories, attributes, fields, loading, attrLoading, ...rest } = mp
        return {
          ...rest,
          attribute: rest.attribute.filter(a => a),
        }
      })
      
      this.$emit('input', cleanedData)
      this.$emit('add', cleanedData)
    },
  },
}
</script>

<style lang="scss">
.selected-channels {
  display: flex !important;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100% !important;
}

.channel-list {
  display: grid !important;
  grid-template-columns: repeat(3, 140px);
  gap: 12px;
  justify-content: center;
  width: 100% !important;
  margin-bottom: 15px !important;
}

.selected-channels .list-channel,
.channel-list .ant-checkbox-wrapper {
  display: block !important;
  position: relative !important;
  width: 140px;
  height: 140px;
  padding: 15px;
  border: 1px solid #dedede;
  border-radius: 12px;
  text-align: center;
}

.selected-channels img,
.channel-list img {
  height: 55px;
  width: 55px;
  margin: 0 auto 10px;
}

.selected-channels p,
.channel-list p {
  font-size: 13px;
  text-align: center;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.selected-channels .channel-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 40px;
  line-height: 1.4;
}

.selected-channels {
  padding: 0;
  list-style-type: none;

  .list-channel .anticon-close-circle {
    position: absolute;
    top: -8px;
    right: -8px;
    font-size: 24px;
    cursor: pointer;

    &:hover {
      svg {
        fill: #ff0a54;
      }
    }

    svg {
      fill: #4d4d4d;
    }
  }

  .list-channel .anticon-plus {
    margin: 10px 0 20px;
    font-size: 35px;
    color: rgb(153, 153, 153);
  }
}

.channel-list {
  margin-bottom: 15px !important;

  .ant-checkbox-wrapper {
    margin: 0 12px 12px 0 !important;
    cursor: pointer;

    &.ant-checkbox-wrapper-checked {
      background-color: #efefef;
      border-color: #FF0A54;
    }

    span {
      margin: 0;
      padding: 0;
    }
  }

  .ant-checkbox::after,
  .ant-checkbox-input,
  .ant-checkbox-inner {
    display: none !important;
  }
}
</style>

<style lang="scss">
.channel-modal-container {
  display: flex;
  flex-direction: column;
  max-height: calc(90vh - 120px); /* Accurate viewport height minus modal header/footer margins */
}

.channel-list-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-right: 4px; /* for scrollbar spacing */
}

.channel-modal-footer {
  flex-shrink: 0;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.sticky-collapse .ant-collapse-header {
  position: sticky !important;
  top: 0;
  z-index: 10;
  background-color: #f7f7f7;
}

.selected-summary {
  background: #f5f8fa;
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  padding: 12px 16px;
  /* margin-top: 16px; */
}

.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  color: #333;
}

.summary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 120px;
  overflow-y: auto;
  padding-bottom: 4px;
}

.channel-mini-tag {
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 4px 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 1.2;
  min-width: 120px;
  flex: 0 0 auto;
  position: relative;
  transition: all 0.2s;

  &:hover {
    border-color: #ff0a54;
    .tag-close {
      opacity: 1;
    }
  }
}

.tag-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-right: 8px;
}

.tag-title {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.tag-code {
  font-size: 10px;
  color: #999;
  font-family: monospace;
}

.tag-close {
  font-size: 14px;
  color: #bfbfbf;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #ff4d4f;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all .3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
